/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Purpose, type PhoneVerificationChannel } from '../../../managers/sso';
import {
  describeSsoError,
  type ResolvedSsoError,
} from '../../../utils/ssoErrors';
import {
  formatMobilePhone,
  normalizeDialingCode,
} from '../../../utils/phoneUtils';
import {
  buildPostVerificationProfileBody,
  type PostVerificationProfileFields,
} from '../../../utils/verifyPhoneOtpUtils';
import { resolvePrimaryOtpChannel } from '../../../widgets/VerifyPhoneView/types';
import type { OnOtpVerified, OtpFlow } from '../types';
import { useUpdateUser } from '../../account';
import {
  useRequestPhoneNumberOtp,
  useVerifyWhatsappOTPByLoggedInUser,
  useWhatsappOtpForPhoneNumberUpdate,
} from '../api';

const DEFAULT_CULTURE_CODE = 'en-GB';

export interface UsePhoneVerificationOtpFlowOptions {
  displayPhoneNumber?: string;
  displayDialingCode?: string;
  resolvedChannels: PhoneVerificationChannel[];
  cultureCode?: string;
  profileUpdate?: PostVerificationProfileFields;
  startTimer: () => void;
  onVerified?: OnOtpVerified;
}

export interface PhoneVerificationOtpFlow extends OtpFlow {
  requestOtpViaChannel: (channel?: PhoneVerificationChannel) => void;
}

export function usePhoneVerificationOtpFlow({
  displayPhoneNumber,
  displayDialingCode,
  resolvedChannels,
  cultureCode,
  profileUpdate,
  startTimer,
  onVerified,
}: UsePhoneVerificationOtpFlowOptions): PhoneVerificationOtpFlow {
  const { t } = useTranslation('account');
  const primaryChannel = resolvePrimaryOtpChannel(resolvedChannels);
  const [localError, setLocalError] = useState<ResolvedSsoError>();
  const [activeChannel, setActiveChannel] = useState<
    PhoneVerificationChannel | undefined
  >(primaryChannel);
  const [whatsappVerificationToken, setWhatsappVerificationToken] = useState<
    string | undefined
  >();
  const profileUpdateRef = useRef(profileUpdate);
  profileUpdateRef.current = profileUpdate;

  const { mutate: requestPhoneOtp } =
    useRequestPhoneNumberOtp('VerifyPhoneView');
  const { mutate: requestWhatsappOtp } =
    useWhatsappOtpForPhoneNumberUpdate('VerifyPhoneView');
  const { mutate: verifyWhatsappLoggedInOtp } =
    useVerifyWhatsappOTPByLoggedInUser('VerifyPhoneView');
  const { mutate: updateUser, isPending: isUpdateUserPending } =
    useUpdateUser('VerifyPhoneView');

  const setRequestError = useCallback(
    (error: unknown, fallback: string) =>
      setLocalError(describeSsoError(error, t, fallback)),
    [t],
  );

  const requestOtpViaChannel = useCallback(
    (channel?: PhoneVerificationChannel) => {
      const selectedChannel = channel ?? primaryChannel;

      if (!selectedChannel) {
        setLocalError({ message: t('verifyPhonePage.requestFailed') });
        return;
      }

      if (!displayPhoneNumber || !displayDialingCode) {
        setLocalError({ message: t('verifyPhonePage.missingPhoneNumber') });
        return;
      }

      setActiveChannel(selectedChannel);
      const normalizedDialingCode = normalizeDialingCode(displayDialingCode);
      const mobilePhone = formatMobilePhone(
        displayDialingCode,
        displayPhoneNumber,
      );

      if (selectedChannel === 'sms') {
        requestPhoneOtp(
          {
            otpPurpose: Purpose.PHONE_VERIFICATION,
            requestOtpBody: {
              dialingCode: normalizedDialingCode,
              phoneNumber: displayPhoneNumber.trim(),
              cultureCode: cultureCode || DEFAULT_CULTURE_CODE,
            },
          },
          {
            onSuccess: () => {
              setLocalError(undefined);
              startTimer();
            },
            onError: (error: unknown) =>
              setRequestError(error, t('verifyPhonePage.requestFailed')),
          },
        );
        return;
      }

      requestWhatsappOtp(
        {
          mobilePhone,
          cultureCode: cultureCode || DEFAULT_CULTURE_CODE,
          platform: selectedChannel,
          purpose: Purpose.PHONE_VERIFICATION,
        },
        {
          onSuccess: (data) => {
            setWhatsappVerificationToken(data?.verificationToken);
            setLocalError(undefined);
            startTimer();
          },
          onError: (error: unknown) =>
            setRequestError(error, t('verifyPhonePage.verificationFailed')),
        },
      );
    },
    [
      cultureCode,
      displayDialingCode,
      displayPhoneNumber,
      primaryChannel,
      requestPhoneOtp,
      requestWhatsappOtp,
      setRequestError,
      startTimer,
      t,
    ],
  );

  // isUpdateUserPending ends with the request; the sign-in tail runs after it.
  const [isFinishing, setIsFinishing] = useState(false);

  const finishWithProfilePatch = useCallback(() => {
    setIsFinishing(true);
    const updateRequestBody = buildPostVerificationProfileBody(
      profileUpdateRef.current,
    );

    const fail = (error: unknown) => {
      setIsFinishing(false);
      setRequestError(error, t('verifyPhonePage.verificationFailed'));
    };

    // Left true on success: the tail has navigated and this screen is unmounting.
    const finish = () => Promise.resolve(onVerified?.()).catch(fail);

    if (Object.keys(updateRequestBody).length === 0) {
      void finish();
      return;
    }

    updateUser(updateRequestBody, {
      onSuccess: () => void finish(),
      onError: fail,
    });
  }, [onVerified, setRequestError, t, updateUser]);

  const submitOtp = useCallback(
    (otp: string) => {
      if (!displayPhoneNumber || !displayDialingCode) {
        setLocalError({ message: t('verifyPhonePage.missingPhoneNumber') });
        return;
      }

      if (!activeChannel) {
        setLocalError({ message: t('verifyPhonePage.requestFailed') });
        return;
      }

      if (activeChannel === 'whatsapp') {
        if (!whatsappVerificationToken) {
          setLocalError({ message: t('verifyPhonePage.requestFailed') });
          return;
        }
        verifyWhatsappLoggedInOtp(
          { otp, verificationToken: whatsappVerificationToken },
          {
            onSuccess: () => finishWithProfilePatch(),
            onError: (error: unknown) =>
              setRequestError(error, t('verifyPhonePage.verificationFailed')),
          },
        );
        return;
      }

      updateUser(
        {
          otp,
          dialingCode: normalizeDialingCode(displayDialingCode),
          mobilePhone: formatMobilePhone(
            displayDialingCode,
            displayPhoneNumber,
          ),
        },
        {
          onSuccess: () => finishWithProfilePatch(),
          onError: (error: unknown) =>
            setRequestError(error, t('verifyPhonePage.verificationFailed')),
        },
      );
    },
    [
      activeChannel,
      displayDialingCode,
      displayPhoneNumber,
      finishWithProfilePatch,
      setRequestError,
      t,
      updateUser,
      verifyWhatsappLoggedInOtp,
      whatsappVerificationToken,
    ],
  );

  const requestOtp = useCallback(
    () => requestOtpViaChannel(activeChannel),
    [requestOtpViaChannel, activeChannel],
  );

  const clearError = useCallback(() => setLocalError(undefined), []);

  return {
    submitOtp,
    requestOtp,
    requestOtpViaChannel,
    errorMessage: localError?.message,
    errorCode: localError?.code,
    isLoading: isUpdateUserPending || isFinishing,
    clearError,
  };
}
